import type { Note } from "@/store/note";
import { markdownLineToPlainText } from "./markdown-text";

type SearchValue = string | null | undefined;
type NoteSearchable = Pick<Note, "title" | "content" | "description" | "tags" | "url" | "filePath">;
type NoteSearchField = {
    value: SearchValue;
    weight: number;
};

const normalizeSearchText = (value: SearchValue): string => {
    if (typeof value !== "string") return "";
    return value.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();
};

const isWordBoundary = (character: string | undefined): boolean =>
    character === undefined || !/[\p{L}\p{N}]/u.test(character);

/** Fuzzy scoring allocates per character, so long fields are only scored over their first characters. */
const SEARCH_TEXT_LIMIT = 20_000;

const isSubsequence = (query: string, candidate: string): boolean => {
    let position = 0;
    for (const character of query) {
        position = candidate.indexOf(character, position);
        if (position === -1) return false;
        position += character.length;
    }
    return true;
};

const getFzfRawScore = (query: string, candidate: SearchValue): number | null => {
    const normalizedQuery = normalizeSearchText(query).trim();
    const normalizedCandidate = normalizeSearchText(candidate);
    if (!normalizedQuery) return 0;
    if (!normalizedCandidate) return null;
    return scoreNormalized(normalizedQuery, normalizedCandidate);
};

const scoreNormalized = (normalizedQuery: string, normalizedCandidate: string): number | null => {
    if (!isSubsequence(normalizedQuery, normalizedCandidate)) return null;

    const queryCharacters = Array.from(normalizedQuery);
    const candidateCharacters = Array.from(normalizedCandidate);
    const scoreCharacter = (candidateIndex: number): number => {
        let score = 1;
        if (isWordBoundary(candidateCharacters[candidateIndex - 1])) score += 8;
        return score - candidateIndex * 0.01;
    };

    let scores = Array<number | null>(candidateCharacters.length).fill(null);
    for (let candidateIndex = 0; candidateIndex < candidateCharacters.length; candidateIndex++) {
        if (candidateCharacters[candidateIndex] === queryCharacters[0]) {
            scores[candidateIndex] = scoreCharacter(candidateIndex);
        }
    }

    for (let queryIndex = 1; queryIndex < queryCharacters.length; queryIndex++) {
        const nextScores = Array<number | null>(candidateCharacters.length).fill(null);
        let bestPreviousScore: number | null = null;

        for (let candidateIndex = 0; candidateIndex < candidateCharacters.length; candidateIndex++) {
            const previousScore = candidateIndex > 0 ? (scores[candidateIndex - 1] ?? null) : null;
            if (previousScore !== null) {
                bestPreviousScore =
                    bestPreviousScore === null ? previousScore : Math.max(bestPreviousScore, previousScore);
            }
            if (candidateCharacters[candidateIndex] !== queryCharacters[queryIndex]) continue;

            const consecutiveScore = candidateIndex > 0 ? (scores[candidateIndex - 1] ?? null) : null;
            let bestScore = bestPreviousScore;
            if (consecutiveScore !== null) {
                const consecutiveMatchScore = consecutiveScore + 4;
                if (bestScore === null || consecutiveMatchScore > bestScore) bestScore = consecutiveMatchScore;
            }
            if (bestScore !== null) nextScores[candidateIndex] = bestScore + scoreCharacter(candidateIndex);
        }
        scores = nextScores;
    }

    const score = scores.reduce<number | null>((best, current) => {
        if (current === null) return best;
        if (best === null) return current;
        return Math.max(best, current);
    }, null);
    if (score === null) return null;

    let totalScore = score;
    if (normalizedCandidate.startsWith(normalizedQuery)) totalScore += 16;
    if (queryCharacters.length === candidateCharacters.length) totalScore += 4;
    return totalScore - candidateCharacters.length * 0.001;
};

export function fzfScore(query: string, candidate: SearchValue): number | null {
    return getFzfRawScore(query, candidate);
}

const MIN_FZF_ACCURACY = 80;

const fzfAccuracy = (
    query: string,
    candidate: SearchValue,
    score = fzfScore(query, candidate),
    perfectScore = fzfScore(query, query),
): number => {
    const normalizedQuery = normalizeSearchText(query).trim();
    const normalizedCandidate = normalizeSearchText(candidate);
    if (!normalizedQuery || !normalizedCandidate) return 0;
    if (score === null || perfectScore === null || perfectScore <= 0) return 0;

    const relativeScore = Math.max(0, Math.min(100, (score / perfectScore) * 100));
    // A contiguous term is an intentional match even when it appears in a longer field.
    return normalizedCandidate.includes(normalizedQuery) ? Math.max(MIN_FZF_ACCURACY, relativeScore) : relativeScore;
};

const getNoteSearchFields = (note: NoteSearchable): NoteSearchField[] => [
    { value: note.title, weight: 30 },
    { value: note.description, weight: 20 },
    { value: note.tags.join(" "), weight: 20 },
    { value: note.content, weight: 10 },
    { value: note.url, weight: 5 },
    { value: note.filePath, weight: 5 },
];

const getNoteSearchScore = (note: NoteSearchable, query: string): number | null => {
    const normalizedQuery = normalizeSearchText(query).trim();
    if (!normalizedQuery) return 0;

    let bestScore: number | null = null;
    const perfectScore = scoreNormalized(normalizedQuery, normalizedQuery);

    for (const field of getNoteSearchFields(note)) {
        const text = normalizeSearchText(field.value);
        if (!text) continue;
        // a contiguous term is an intentional match wherever it appears, even past the scored window
        const contiguous = text.includes(normalizedQuery);
        const window = text.length > SEARCH_TEXT_LIMIT ? text.slice(0, SEARCH_TEXT_LIMIT) : text;
        const score = scoreNormalized(normalizedQuery, window);
        let accuracy: number;
        if (score === null) {
            if (!contiguous) continue;
            accuracy = MIN_FZF_ACCURACY;
        } else {
            accuracy = fzfAccuracy(normalizedQuery, window, score, perfectScore);
            if (contiguous) accuracy = Math.max(MIN_FZF_ACCURACY, accuracy);
        }
        if (accuracy < MIN_FZF_ACCURACY) continue;

        const weightedScore = accuracy + field.weight;
        if (bestScore === null || weightedScore > bestScore) bestScore = weightedScore;
    }

    return bestScore;
};

export function getNoteSearchSnippet(content: string, query: string, maxLength = 120): string {
    const lines = (content.length > SEARCH_TEXT_LIMIT ? content.slice(0, SEARCH_TEXT_LIMIT) : content)
        .split(/\r?\n/)
        .map((line) =>
            markdownLineToPlainText(line.replace(/`/g, ""))
                .replace(/^\s{0,3}#{1,6}\s+/, "")
                .replace(/^\s{0,3}>\s?/, "")
                .trim(),
        )
        .filter(Boolean);
    if (lines.length === 0) return "No preview available.";

    const normalizedQuery = normalizeSearchText(query).trim();
    const queryTokens = normalizedQuery.split(/\s+/u).filter(Boolean);
    const snippet =
        lines.find((line) => normalizeSearchText(line).includes(normalizedQuery)) ??
        lines.find((line) => queryTokens.some((token) => normalizeSearchText(line).includes(token))) ??
        lines[0] ??
        "No preview available.";
    const limit = Math.max(1, Math.floor(maxLength));
    if (snippet.length <= limit) return snippet;

    let matchIndex = -1;
    if (query.trim()) {
        const lowerSnippet = snippet.toLowerCase();
        matchIndex = lowerSnippet.indexOf(query.trim().toLowerCase());
        if (matchIndex < 0) {
            const firstToken = query.trim().split(/\s+/u)[0]?.toLowerCase() ?? "";
            matchIndex = lowerSnippet.indexOf(firstToken);
        }
    }
    const start = Math.max(0, Math.min(snippet.length - limit, matchIndex - 32));
    const end = Math.min(snippet.length, start + limit);
    return `${start > 0 ? "…" : ""}${snippet.slice(start, end)}${end < snippet.length ? "…" : ""}`;
}

export function filterNotesByQuery<T extends NoteSearchable>(notes: readonly T[], query: string): T[] {
    if (!normalizeSearchText(query).trim()) return [...notes];

    const matches: Array<{ note: T; score: number; index: number }> = [];
    for (const [index, note] of notes.entries()) {
        const score = getNoteSearchScore(note, query);
        if (score !== null) matches.push({ note, score, index });
    }

    return matches.sort((left, right) => right.score - left.score || left.index - right.index).map(({ note }) => note);
}
